<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCompaniesStore } from 'dashboard/stores/companies';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedCompany: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const companiesStore = useCompaniesStore();

const searchQuery = ref('');
const editingKey = ref(null);
const editValue = ref('');
const isUpdating = ref(false);

const metadata = computed(() => {
  return props.selectedCompany?.metadata || {};
});

const hasMetadata = computed(() => {
  return Object.keys(metadata.value).length > 0;
});

const metadataEntries = computed(() => {
  return Object.entries(metadata.value).filter(([key]) =>
    key.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const startEditing = (key, value) => {
  editingKey.value = key;
  editValue.value = value;
};

const cancelEditing = () => {
  editingKey.value = null;
  editValue.value = '';
};

const saveAttribute = async (key) => {
  isUpdating.value = true;
  try {
    const newMetadata = {
      ...metadata.value,
      [key]: editValue.value,
    };
    await companiesStore.update(props.selectedCompany.id, { metadata: newMetadata });
    useAlert(t('COMPANIES.SIDEBAR.ATTRIBUTES.UPDATE_SUCCESS'));
    editingKey.value = null;
  } catch (error) {
    useAlert(t('COMPANIES.SIDEBAR.ATTRIBUTES.UPDATE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const deleteAttribute = async (keyToDelete) => {
  isUpdating.value = true;
  try {
    const { [keyToDelete]: _, ...rest } = metadata.value;
    await companiesStore.update(props.selectedCompany.id, { metadata: rest });
    useAlert(t('COMPANIES.SIDEBAR.ATTRIBUTES.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.SIDEBAR.ATTRIBUTES.DELETE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const showAddForm = ref(false);
const newKey = ref('');
const newValue = ref('');

const cancelAdd = () => {
  showAddForm.value = false;
  newKey.value = '';
  newValue.value = '';
};

const addAttribute = async () => {
  if (!newKey.value.trim()) return;
  
  isUpdating.value = true;
  try {
    const newMetadata = {
      ...metadata.value,
      [newKey.value.trim()]: newValue.value,
    };
    await companiesStore.update(props.selectedCompany.id, { metadata: newMetadata });
    useAlert(t('COMPANIES.SIDEBAR.ATTRIBUTES.ADD_SUCCESS'));
    cancelAdd();
  } catch (error) {
    useAlert(t('COMPANIES.SIDEBAR.ATTRIBUTES.ADD_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 px-6 py-6">
    <div class="flex items-center justify-between">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('COMPANIES.SIDEBAR.ATTRIBUTES.TITLE') }}
      </h4>
      <Button
        v-if="!showAddForm"
        icon="i-lucide-plus"
        variant="ghost"
        size="xs"
        @click="showAddForm = true"
      />
    </div>

    <!-- Add New Attribute Form -->
    <div v-if="showAddForm" class="flex flex-col gap-2 p-3 bg-n-alpha-black2 rounded-lg">
      <Input
        v-model="newKey"
        :placeholder="t('COMPANIES.SIDEBAR.ATTRIBUTES.KEY_PLACEHOLDER')"
        size="sm"
      />
      <Input
        v-model="newValue"
        :placeholder="t('COMPANIES.SIDEBAR.ATTRIBUTES.VALUE_PLACEHOLDER')"
        size="sm"
      />
      <div class="flex justify-end gap-2">
        <Button
          :label="t('COMPANIES.CANCEL')"
          variant="ghost"
          size="xs"
          @click="cancelAdd"
        />
        <Button
          :label="t('COMPANIES.ADD')"
          size="xs"
          :is-loading="isUpdating"
          :disabled="!newKey.trim()"
          @click="addAttribute"
        />
      </div>
    </div>

    <!-- Search -->
    <div v-if="hasMetadata" class="relative">
      <span class="absolute i-lucide-search size-4 top-2.5 left-3 text-n-slate-11" />
      <input
        v-model="searchQuery"
        type="search"
        :placeholder="t('COMPANIES.SIDEBAR.ATTRIBUTES.SEARCH_PLACEHOLDER')"
        class="w-full h-9 py-2 pl-10 pr-2 text-sm reset-base outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
      />
    </div>

    <!-- Attributes List -->
    <div v-if="hasMetadata" class="flex flex-col gap-2">
      <div
        v-for="[key, value] in metadataEntries"
        :key="key"
        class="flex flex-col gap-2 p-3 bg-n-alpha-black2 rounded-lg group"
      >
        <div v-if="editingKey !== key" class="flex items-center justify-between">
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <span class="text-xs text-n-slate-11 capitalize">{{ key }}</span>
            <span class="text-sm text-n-slate-12 truncate">{{ value }}</span>
          </div>
          <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              icon="i-lucide-pencil"
              variant="ghost"
              size="xs"
              @click="startEditing(key, value)"
            />
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              size="xs"
              color="ruby"
              @click="deleteAttribute(key)"
            />
          </div>
        </div>
        
        <!-- Edit Mode -->
        <div v-else class="flex flex-col gap-2">
          <span class="text-xs text-n-slate-11 capitalize">{{ key }}</span>
          <Input
            v-model="editValue"
            size="sm"
            autofocus
          />
          <div class="flex justify-end gap-2">
            <Button
              :label="t('COMPANIES.CANCEL')"
              variant="ghost"
              size="xs"
              @click="cancelEditing"
            />
            <Button
              :label="t('COMPANIES.SAVE')"
              size="xs"
              :is-loading="isUpdating"
              @click="saveAttribute(key)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex flex-col items-center justify-center py-8 text-center">
      <span class="i-lucide-tags text-n-slate-9 size-8 mb-2" />
      <p class="text-sm text-n-slate-11">
        {{ t('COMPANIES.SIDEBAR.ATTRIBUTES.EMPTY_STATE') }}
      </p>
      <p class="text-xs text-n-slate-10 mt-1">
        {{ t('COMPANIES.SIDEBAR.ATTRIBUTES.EMPTY_HINT') }}
      </p>
    </div>
  </div>
</template>
